import { useCallback, useEffect } from 'react'
import AwsS3Multipart from '@uppy/aws-s3-multipart'
import Dropbox from '@uppy/dropbox'

/**
 * Hook for managing Uppy plugins including initialization and configuration
 *
 * @param {Object} uppy - Uppy instance to attach plugins to
 * @param {string} API_GATEWAY_URL - API Gateway URL for plugin configuration
 * @param {Function} createMultipartUpload - Function to create multipart upload sessions
 * @returns {Object} Hook interface containing plugin management functions
 */
const useUppyPlugins = (uppy, API_GATEWAY_URL, createMultipartUpload) => {
  /**
   * Generic plugin initialization function with error handling
   * @param {Function} plugin - Plugin constructor function
   * @param {Object} options - Plugin configuration options
   * @param {string} name - Plugin name for logging
   */
  const initializePlugin = useCallback(
    (plugin, options, name) => {
      try {
        if (uppy) {
          uppy.use(plugin, options)
        }
      } catch (error) {
        console.error(`Error initializing ${name} plugin:`, error)
      }
    },
    [uppy]
  )

  /**
   * Initialize AWS S3 Multipart plugin
   */
  const initializeS3Plugin = useCallback(() => {
    if (!uppy || !API_GATEWAY_URL || !createMultipartUpload) return

    initializePlugin(
      AwsS3Multipart,
      {
        limit: 4,
        companionUrl: API_GATEWAY_URL,
        createMultipartUpload: createMultipartUpload
      },
      'AwsS3Multipart'
    )
  }, [uppy, API_GATEWAY_URL, createMultipartUpload, initializePlugin])

  /**
   * Initialize Dropbox plugin
   */
  const initializeDropboxPlugin = useCallback(() => {
    if (!uppy || !API_GATEWAY_URL) return

    initializePlugin(
      Dropbox,
      {
        companionUrl: API_GATEWAY_URL,
        companionHeaders: {
          'access-control-request-headers':
            'content-type, accept, uppy-auth-token'
        }
      },
      'Dropbox'
    )
  }, [uppy, API_GATEWAY_URL, initializePlugin])

  /**
   * Initialize Instagram plugin (currently commented out)
   */
  const initializeInstagramPlugin = useCallback(() => {
    // TODO: Implement Instagram plugin when needed
    // initializePlugin(Instagram, {
    //   companionUrl: API_GATEWAY_URL
    // }, 'Instagram');
  }, [])

  /**
   * Initialize Google Drive plugin (currently commented out)
   */
  const initializeGoogleDrivePlugin = useCallback(() => {
    // TODO: Implement Google Drive plugin when needed
    // initializePlugin(GoogleDrive, {
    //   companionUrl: 'http://localhost:3020'
    // }, 'GoogleDrive');
  }, [])

  /**
   * Initialize Facebook plugin (currently commented out)
   */
  const initializeFacebookPlugin = useCallback(() => {
    // TODO: Implement Facebook plugin when needed
    // initializePlugin(Facebook, {
    //   companionUrl: API_GATEWAY_URL,
    //   companionHeaders: {
    //     "access-control-request-headers": "content-type, accept, uppy-auth-token"
    //   }
    // }, 'Facebook');
  }, [])

  /**
   * Initialize all active plugins
   */
  const initializeAllPlugins = useCallback(() => {
    initializeS3Plugin()
    initializeDropboxPlugin()
    // Add other plugins as needed
  }, [initializeS3Plugin, initializeDropboxPlugin])

  /**
   * Initialize plugins when uppy instance is available
   */
  useEffect(() => {
    if (uppy && API_GATEWAY_URL && createMultipartUpload) {
      initializeAllPlugins()
    }
  }, [uppy, API_GATEWAY_URL, createMultipartUpload, initializeAllPlugins])

  return {
    initializePlugin,
    initializeS3Plugin,
    initializeDropboxPlugin,
    initializeInstagramPlugin,
    initializeGoogleDrivePlugin,
    initializeFacebookPlugin,
    initializeAllPlugins
  }
}

export default useUppyPlugins
